import type { ImageKey } from "../game/assets";
import type { MapDef, PlanetType } from "../sim";

export interface PreviewShape {
	kind: "planet" | "spawn";
	x: number;
	y: number;
	r: number;
	planetType?: PlanetType;
}

/** Smallest radius (px) so tiny planets and spawn points stay visible in a small preview. */
const MIN_PLANET_PX = 3;
const SPAWN_POINT_PX = 4;

/** Map geometry scaled into a square preview of `sizePx`, keeping the aspect ratio centred. */
export function previewShapes(map: MapDef, sizePx: number): PreviewShape[] {
	const scale = sizePx / Math.max(map.size.w, map.size.h);
	const offX = (sizePx - map.size.w * scale) / 2;
	const offY = (sizePx - map.size.h * scale) / 2;
	return [
		...map.planets.map(
			(p): PreviewShape => ({
				kind: "planet",
				planetType: p.type,
				x: offX + p.x * scale,
				y: offY + p.y * scale,
				r: Math.max(p.radius * scale, MIN_PLANET_PX),
			}),
		),
		...map.spawnPoints.map(
			(s): PreviewShape => ({
				kind: "spawn",
				x: offX + s.x * scale,
				y: offY + s.y * scale,
				r: SPAWN_POINT_PX,
			}),
		),
	];
}

const PLANET_IMAGE: Record<PlanetType, ImageKey> = {
	mars: "planetMars",
	earth: "planetEarth",
	pluto: "planetPluto",
};

export type PreviewImages = Partial<Record<ImageKey, CanvasImageSource>>;

/** Draws a map preview onto a canvas using the original planet art when available. */
export function drawMapPreview(
	canvas: HTMLCanvasElement,
	map: MapDef,
	images: PreviewImages,
): void {
	const ctx = canvas.getContext("2d");
	if (!ctx) return;
	const size = canvas.width;
	ctx.clearRect(0, 0, size, size);

	const bg = images.background;
	if (bg) ctx.drawImage(bg, 0, 0, size, size);
	else {
		ctx.fillStyle = "#05070f";
		ctx.fillRect(0, 0, size, size);
	}

	for (const shape of previewShapes(map, size)) {
		if (shape.kind === "planet") {
			const img = shape.planetType ? images[PLANET_IMAGE[shape.planetType]] : undefined;
			if (img) ctx.drawImage(img, shape.x - shape.r, shape.y - shape.r, shape.r * 2, shape.r * 2);
			else {
				ctx.fillStyle = "#7f8fb8";
				ctx.beginPath();
				ctx.arc(shape.x, shape.y, shape.r, 0, Math.PI * 2);
				ctx.fill();
			}
		} else {
			ctx.strokeStyle = "#7fd0ff";
			ctx.lineWidth = 2;
			ctx.beginPath();
			ctx.arc(shape.x, shape.y, shape.r, 0, Math.PI * 2);
			ctx.stroke();
		}
	}
}
